import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommonExplainModule } from '../../common-explain/common-explain.module';
import { Prompt, PromptInterface } from '../../models/prompt';
import { MatIcon } from '@angular/material/icon';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SmartSearchSource, SMART_SEARCH_SESSIONS_KEY, TAB_ID_KEY, SmartSearchConfig } from '../../shared/services/smart-search.service';

import { SmartSearchPromptComponent } from '../smart-search-prompt/smart-search-prompt.component';
import { SmartSearchPromptModule } from '../../shared/modules/smart-search-prompt/smart-search-prompt.module';
import { SmartSearchPromptService } from '../../shared/services/smart-search-prompt.service';

@Component({
  selector: 'app-smart-search-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, CommonExplainModule, MatIcon, NgbTooltip, SmartSearchPromptModule],
  templateUrl: './smart-search-panel.component.html',
  styleUrls: ['./smart-search-panel.component.scss'],
})
export class SmartSearchPanelComponent implements OnInit, OnChanges {
  @Input() searchConfig: SmartSearchConfig = {};
  @Input() count = 0;
  @ViewChild(SmartSearchPromptComponent) editor?: SmartSearchPromptComponent;
  @ViewChild('seeMoreContainer', { read: ElementRef }) seeMoreContainer?: ElementRef;
  @ViewChild('promptsList', { read: ElementRef }) promptsList?: ElementRef;
  @ViewChild('promptsSection', { read: ElementRef }) promptsSection?: ElementRef;

  selectedSources = {
    [SmartSearchSource.PRESS]: true,
    [SmartSearchSource.ADMIN_DOC]: true,
    [SmartSearchSource.WEB]: true,
  };
  selectedPrompt?: Prompt;
  prompt = '';
  currentSessionId: string | null = null;
  showSeeMoreDropdown = false;
  maxVisiblePrompts = 0;
  dropdownTop = '0px';
  dropdownRight = '0px';

  protected readonly SmartSearchSource = SmartSearchSource;

  constructor(
    protected promptService: SmartSearchPromptService,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      this.recalculateLayout();
    });
  }

  ngOnInit() {
    if (this.searchConfig.impacterId) {
      this.selectedSources[SmartSearchSource.ADMIN_DOC] = false;
      this.updateSmartSearchSession();
    }
    this.initializeTabSessionId();
    this.loadConfigFromStorage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true && changes['isOpen']?.previousValue === false && this.currentSessionId) {
      this.updateSmartSearchSession();
    }
  }

  initializeTabSessionId(): void {
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = `tab_${Date.now()}`;
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }
    this.currentSessionId = tabId;
  }

  loadConfigFromStorage(): void {
    if (!this.currentSessionId) {
      this.initializeTabSessionId();
    }
    if (!this.currentSessionId) {
      return;
    }
    const sessionsJson = localStorage.getItem(SMART_SEARCH_SESSIONS_KEY);
    if (!sessionsJson) {
      return;
    }
    const sessions = JSON.parse(sessionsJson);
    const savedData = sessions[this.currentSessionId];
    if (savedData) {
      this.restoreSavedData(savedData);
    }
  }

  private restoreSavedData(savedData: any): void {
    this.restorePrompt(savedData);
    this.restoreSources(savedData);
  }

  private restorePrompt(savedData: any): void {
    if (savedData.promptBase) {
      this.selectedPrompt = {
        text: savedData.promptBase,
        title: '',
      };
      this.prompt = savedData.promptBase;
    }
  }

  private restoreSources(savedData: any): void {
    if (savedData.sources && Array.isArray(savedData.sources)) {
      this.selectedSources = {
        [SmartSearchSource.PRESS]: savedData.sources.includes(SmartSearchSource.PRESS),
        [SmartSearchSource.ADMIN_DOC]: savedData.sources.includes(SmartSearchSource.ADMIN_DOC),
        [SmartSearchSource.WEB]: savedData.sources.includes(SmartSearchSource.WEB),
      };
    }
  }

  toggleSourceStatus(source: SmartSearchSource): void {
    this.selectedSources[source] = !this.selectedSources[source];
    this.updateSmartSearchSession();
  }

  selectPrompt(prompt: Prompt): void {
    this.selectedPrompt = prompt;
    this.prompt = prompt.text;
    this.updateSmartSearchSession();
  }

  private recalculateLayout(): void {
    this.maxVisiblePrompts = this.promptService.prompts().length;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      if (this.promptsList?.nativeElement) {
        this.checkOverflow();
        this.cdr.detectChanges();
      }
    });
  }

  private saveAndUpdatePrompts(): void {
    if (this.selectedPrompt) {
      this.promptService.savePromptUsage(this.selectedPrompt);
      this.promptService.prompts.set(this.promptService.sortPromptsByUsage(this.promptService.prompts()));
      this.recalculateLayout();
    }
  }

  onPromptTextChange(prompt: string): void {
    this.prompt = prompt.trim();
    this.updateSmartSearchSession();
  }

  canTriggerSmartSearch(): boolean {
    const hasPrompt = this.prompt.length > 0;
    const selectedSourcesList = Object.entries(this.selectedSources)
      .filter(([_, isSelected]) => isSelected)
      .map(([source]) => source);
    if (!hasPrompt) {
      return false;
    }
    if (selectedSourcesList.length === 0) {
      return false;
    }
    if (this.editor?.hasEmptyTags()) {
      return false;
    }
    if (!this.searchConfig.territories?.length) {
      return false;
    }
    if (!this.count) {
      return false;
    }
    return !(selectedSourcesList.length === 1 && selectedSourcesList[0] === SmartSearchSource.WEB);
  }

  getTooltipMessage(): string {
    const hasPrompt = this.prompt.length > 0;
    const selectedSourcesList = Object.entries(this.selectedSources)
      .filter(([_, isSelected]) => isSelected)
      .map(([source]) => source);

    if (!hasPrompt) {
      return 'search.smart-search-panel.specify-analysis';
    }
    if (selectedSourcesList.length === 0) {
      return 'search.smart-search-panel.select-at-least-one-source';
    }
    if (this.editor?.hasEmptyTags()) {
      return 'search.smart-search-panel.fill-tags';
    }
    if (!this.searchConfig.territories?.length) {
      return 'search.smart-search-panel.select-at-least-one-territory';
    }
    if (!this.count) {
      return 'search.smart-search-panel.no-results-modify-filters';
    }
    if (selectedSourcesList.length === 1 && selectedSourcesList[0] === SmartSearchSource.WEB) {
      return 'search.smart-search-panel.select-another-source';
    }
    return '';
  }

  shouldShowTooltip(): boolean {
    return !this.canTriggerSmartSearch();
  }

  TriggerSmartSearch(): void {
    if (!this.currentSessionId) {
      return;
    }

    this.saveAndUpdatePrompts();

    const searchSessionId = `search_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    this.updateSmartSearchSession();
    const sessionsStr = localStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessions = JSON.parse(sessionsStr);
    const currentConfig = sessions[this.currentSessionId];
    if (currentConfig) {
      sessions[searchSessionId] = currentConfig;
      localStorage.setItem(SMART_SEARCH_SESSIONS_KEY, JSON.stringify(sessions));
    }
    window.open(`/smart-search?sessionId=${searchSessionId}`, '_blank');
  }

  checkOverflow(): void {
    let hasOverflow = false;
    const container = this.promptsList?.nativeElement;
    const prompts = this.promptService.prompts();

    if (!container || !prompts.length) return;

    const containerRect = container.getBoundingClientRect();

    if (containerRect.width === 0 || containerRect.height === 0) {
      setTimeout(() => this.checkOverflow(), 10);
      return;
    }

    const maxHeight = 74;
    const allButtons = container.querySelectorAll('.prompt-button:not(.see-more-button)');

    if (allButtons.length === 0) {
      this.maxVisiblePrompts = prompts.length;
      return;
    }

    let count = 0;
    let lastButtonOnSecondLine: HTMLElement | null = null;
    let lastButtonIndex = -1;

    allButtons.forEach((button: HTMLElement, index: number) => {
      const bottomInContainer = button.getBoundingClientRect().bottom - containerRect.top;
      if (bottomInContainer > maxHeight) return;
      count++;
      lastButtonIndex = index;
      if (bottomInContainer > maxHeight / 2) {
        lastButtonOnSecondLine = button;
      }
    });

    hasOverflow = count < prompts.length;

    if (hasOverflow && lastButtonOnSecondLine !== null && lastButtonIndex >= 0) {
      const lastButtonRect = (lastButtonOnSecondLine as HTMLElement).getBoundingClientRect();
      const spaceRemaining = containerRect.right - lastButtonRect.right;

      if (spaceRemaining < 136) {
        this.maxVisiblePrompts = Math.max(0, lastButtonIndex);
      } else {
        this.maxVisiblePrompts = count;
      }
    } else {
      this.maxVisiblePrompts = hasOverflow ? count : prompts.length;
    }
  }

  hiddenPrompts(): Prompt[] {
    const hidden = this.promptService.prompts().slice(this.maxVisiblePrompts);
    return hidden.sort((a, b) => {
      const promptTypeDiff = Number(!a.user_id) - Number(!b.user_id);
      return promptTypeDiff || a.title.toLowerCase().localeCompare(b.title.toLowerCase());
    });
  }

  hasMorePrompts(): boolean {
    return this.maxVisiblePrompts < this.promptService.prompts().length;
  }

  toggleSeeMoreDropdown(event: Event): void {
    event.stopPropagation();

    if (!this.showSeeMoreDropdown && this.seeMoreContainer && this.promptsSection) {
      const exButton = this.seeMoreContainer?.nativeElement.querySelector('ex-button.see-more-button');
      const button = exButton ? exButton.querySelector('button') : null;
      if (button) {
        const buttonRect = button.getBoundingClientRect();
        const sectionRect = this.promptsSection.nativeElement.getBoundingClientRect();
        this.dropdownTop = `${buttonRect.bottom - sectionRect.top + 8}px`;
        this.dropdownRight = `${sectionRect.right - buttonRect.right}px`;
      }
    }

    this.showSeeMoreDropdown = !this.showSeeMoreDropdown;
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showSeeMoreDropdown || !this.seeMoreContainer) {
      return;
    }

    const target = event.target as Node;
    const clickedInside = this.seeMoreContainer.nativeElement.contains(target);

    if (!clickedInside) {
      this.showSeeMoreDropdown = false;
      this.cdr.detectChanges();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.promptService.prompts().length > 0) {
      this.recalculateLayout();
    }
  }

  updateSmartSearchSession(): void {
    if (!this.currentSessionId) {
      return;
    }

    const tagsUserInputs = this.editor?.getTagsUserInputs() ?? {};

    const smartSearchConfig = {
      prompt: this.editor?.serialize(undefined, true) ?? '',
      promptBase: this.prompt,
      promptId: this.selectedPrompt?.id,
      promptType: this.selectedPrompt?.id ? (this.selectedPrompt.user_id ? 'user' : 'default') : undefined,
      promptInputs: tagsUserInputs,
      sources: Object.entries({
        ...this.selectedSources,
        ...(this.searchConfig.impacterId ? { [SmartSearchSource.ADMIN_DOC]: false } : {}),
      })
        .filter(([_, isSelected]) => isSelected)
        .map(([source, _]) => source),
      territories: this.searchConfig.territories || [],
      displayedPerimeter: this.searchConfig.displayedPerimeter || null,
      fullPerimeter: this.searchConfig.fullPerimeter || null,
      territoryTree: this.searchConfig.territoryTree || [],
      perimeter: this.searchConfig.perimeter || [],
      topicId: this.searchConfig.topicId || null,
      keywords: this.searchConfig.keywords || '',
      pressSources: this.searchConfig.pressSources || [],
      adminDocSources: this.searchConfig.adminDocSources || [],
      impacterId: this.searchConfig.impacterId || null,
      impacterName: this.searchConfig.impacterName || null,
      period: this.searchConfig.period || null,
    };

    const sessionsStr = localStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessionsJson: Record<string, any> = JSON.parse(sessionsStr);
    sessionsJson[this.currentSessionId] = smartSearchConfig;
    localStorage.setItem(SMART_SEARCH_SESSIONS_KEY, JSON.stringify(sessionsJson));
  }

  editPrompt(prompt: Prompt) {
    const ref = this.promptService.savePrompt(prompt.text, prompt.title, prompt.id);
    ref.result.then((result: PromptInterface | undefined) => {
      if (result?.id) this.promptService.modifyPrompt(result.id, result.title, result.text);
      else if (prompt.id) this.promptService.removePrompt(prompt.id);
      this.recalculateLayout();
    });
  }
}
